import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import AllianceManagerServices from "../../../services/API/AllianceManager";
import { Modal, Button } from "react-bootstrap";

const SignedDocumentList = ({ srnId, readonly }) => {
  const [loader, setLoader] = useState("");
  const [actionLoader, setActionLoader] = useState(null);
  const [rows, setRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [status, setStatus] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const hasChanges =
    rows.length !== savedRows.length ||
    rows.some((row) => {
      if (!row.isExisting) return true;
      const saved = savedRows.find((s) => s.id === row.id);
      if (!saved) return true;
      return (
        row.document_name !== saved.document_name ||
        row.admin_remark !== saved.admin_remark ||
        row.admin_upload !== null
      );
    });

  // STATUS CONFIG
  const statusConfig = {
    "-1": { label: "Awaiting User upload", color: "orange" },
    "0": { label: "Pending", color: "orange" },
    "1": { label: "Accepted", color: "green" },
    "2": { label: "Rejected", color: "red" },
  };

  // CREATE EMPTY ROW
  const createEmptyRow = () => ({
    id: Date.now(),
    document_name: "",
    admin_upload: null,
    admin_file: null,
    admin_file_name: null,
    admin_remark: "",
    user_file: "",
    user_file_name: "",
    user_remark: "",
    selected: false,
    status: -1,
    isExisting: false,
    srn_id: null,
  });

  useEffect(() => {
    if (srnId) fetchExistingData();
  }, [srnId]);

  // FETCH DATA
  const fetchExistingData = async () => {
    try {
      const res = await AllianceManagerServices.getSignedDocuments(srnId);
      if (res?.data) {
        const formatted = res.data.map((item) => ({
          id: item.id,
          document_name: item.document_name || "",
          admin_upload: null,
          admin_file: item.admin_file,
          admin_file_name: item.admin_file_name,
          admin_remark: item.admin_remark || "",
          user_file: item.user_file,
          user_file_name: item.user_file_name,
          user_remark: item.user_remark,
          selected: false,
          status: item.status ?? -1, // ✅ important
          isExisting: true,
          srn_id: item.srn_id,
        }));

        const finalRows = formatted.length ? formatted : [createEmptyRow()];
        setRows(finalRows);
        setSavedRows(finalRows);
      }
    } catch (e) {
      toast.error("Failed to load data");
    }

  };

  // ADD ROW
  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  // REMOVE
  const removeSelectedRows = (rowId = null) => {
    if (rowId !== null) {
      const row = rows.find((r) => r.id === rowId);
      if (!row) return;
      if (row.status !== -1) {
        toast.error("Row can't be removed after user upload");
        return;
      }
      const filtered = rows.filter((r) => r.id !== rowId);
      if (filtered.length === 0) {
        toast.error("At least one row required");
        return;
      }
      setRows(filtered);
      return;
    }

    const selectedRows = rows.filter((row) => row.selected);

    if (selectedRows.length === 0) {
      toast.error("Select at least one row");
      return;
    }

    const validRows = selectedRows.filter((row) => row.status === -1);
    const invalidRows = selectedRows.filter((row) => row.status !== -1);

    if (invalidRows.length > 0) {
      toast.error("Rows can't be removed after user upload");
    }

    if (validRows.length === 0) {
      return;
    }

    const filtered = rows.filter(
      (row) => !(row.selected && row.status === -1)
    );

    if (filtered.length === 0) {
      toast.error("At least one row required");
      return;
    }

    setRows(filtered);
  };

  // CHANGE
  const handleChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  // SELECT
  const handleSelect = (id) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, selected: !row.selected } : row
      )
    );
  };

  // SUBMIT
  const handleSubmit = async () => {
    try {
      if (!validateRows()) return;

      setLoader("Loading...");
      const formData = new FormData();

      const payload = rows.map((row) => ({
        id: row.isExisting ? row.id : null,
        srnId: srnId,
        document_name: row.document_name,
        admin_remark: row.admin_remark,
        status: row.status, // ✅ backend safe
      }));

      formData.append("data", JSON.stringify(payload));

      rows.forEach((row, index) => {
        if (row.admin_upload) {
          formData.append(`admin_upload_${index}`, row.admin_upload);
        }
      });

      const response =
        await AllianceManagerServices.saveSignedDocumentRequest(formData);

      if (response) {
        toast.success("Saved successfully");
        fetchExistingData();
      } else {
        toast.error("Something went wrong");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoader("");
    }

  };

  const validateRows = () => {
    const seenNames = new Set();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      // Document name
      if (!row.document_name.trim()) {
        toast.error(`Row ${i + 1}: Document name is required`);
        return false;
      }

      // Duplicate document name
      const normalizedName = row.document_name.trim().toLowerCase();
      if (seenNames.has(normalizedName)) {
        toast.error(`Row ${i + 1}: Duplicate document name "${row.document_name.trim()}"`);
        return false;
      }
      seenNames.add(normalizedName);

      // File required for new rows
      if (!row.isExisting && !row.admin_upload) {
        toast.error(`Row ${i + 1}: Please upload a file`);
        return false;
      }

      // File validation
      if (row.admin_upload) {
        const file = row.admin_upload;
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

        if (!allowedTypes.includes(file.type)) {
          toast.error(`Row ${i + 1}: Invalid file type`);
          return false;
        }

        // Max size 2MB
        if (file.size > 2 * 1024 * 1024) {
          toast.error(`Row ${i + 1}: File size must be less than 2MB`);
          return false;
        }
      }

      if (!row.admin_remark.trim()) {
        toast.error(`Row ${i + 1}: Remark is required`);
        return false;
      }

      // Remark length
      if (row.admin_remark && row.admin_remark.length > 500) {
        toast.error(`Row ${i + 1}: Remark too long (max 500 chars)`);
        return false;
      }
    }

    return true;
  };

  const downloadSingleFile = async (docId, docName, type) => {
    try {
      const response = await AllianceManagerServices.downloadSignedDocument(
        docId + "/" + type
      );
      if (response) {
        var ext = docName.split(".")[1];
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${docName}.${ext === "xlsx"
            ? "xlsx"
            : ext === "xls"
              ? "xls"
              : ext === "bmp"
                ? "bmp"
                : ext === "pdf"
                  ? "pdf"
                  : ext === "jpeg"
                    ? "jpeg"
                    : ext
          }`
        );
        link.click();
        toast.success("Document Downloaded Successfully");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // APPROVE / DELETE
  const handleSignedDocument = async (id = "", status = "") => {
    const rowId = id ? id : selectedRowId;
    const loaderKey = status == 3 ? `${rowId}-delete` : `${rowId}-approve`;
    try {
      if (status == 2) {
        if (!rejectRemarks.trim()) {
          toast.error("Rejection reason is required");
          return;
        }
      }
      setActionLoader(loaderKey);
      const payload = {
        id: rowId,
        status: status ? status : status,
        reason: rejectRemarks,
      };
      const res = await AllianceManagerServices.handleSignedDocument(payload);
      if (res?.status_code == 200) {
        if (status == 3) {
          toast.success("Document Deleted Successfully");
          fetchExistingData();
        } else if (status == 2) {
          setRejectModal(false);
          setRejectRemarks("");
          setSelectedRowId(null);
          toast.success("Document Rejected Successfully");
          fetchExistingData();
        } else {
          toast.success("Document Approved Successfully");
          fetchExistingData();
        }
      }
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setActionLoader(null);
    }
  };

  // REJECT
  const handleRejectDocument = async () => {
    try {
      if (status == 2) {
        if (!rejectRemarks.trim()) {
          toast.error("Rejection reason is required");
          return;
        }
      }
      setActionLoader(`${selectedRowId}-reject`);
      const payload = {
        id: selectedRowId,
        status: status,
        reason: rejectRemarks,
      };
      const res = await AllianceManagerServices.handleSignedDocument(payload);
      if (res?.status_code == 200) {
        setRejectModal(false);
        setRejectRemarks("");
        setSelectedRowId(null);
        toast.success("Document Rejected Successfully");

        setRows((prev) =>
          prev.map((row) =>
            row.id === selectedRowId
              ? { ...row, status: status }
              : row
          )
        );
      }
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setActionLoader(null);
    }
  };

  return (
    <div className="card card-sm card-shadow mt-4">
      <Modal
        show={rejectModal}
        onHide={() => {
          setRejectModal(false);
          setRejectRemarks("");
          setSelectedRowId(null);
        }}
        animation={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Rejection Remarks</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <textarea
            className="form-control"
            rows="4"
            placeholder="Enter reason for rejection"
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={handleRejectDocument}
            disabled={actionLoader === `${selectedRowId}-reject`}
          >
            {actionLoader === `${selectedRowId}-reject` ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
      <div className="card-body">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="heading_light m-0">
            <i
              className="fa-solid fa-file-signature"
              aria-hidden="true"
              style={{ color: "#64308e" }}
              title="Signed Documents List"
            ></i>
            {" "}{" "}Signed Document List
            {rows?.length > 0 && (() => {
              const existingRows = rows.filter(r => r.isExisting);
              const total = existingRows.length;
              const uploaded = existingRows.filter(r => r.status !== 2 && r.user_file_name !== null).length;
              if (total === 0) return null;
              return (
                <span className="sdl-pill-badge">
                  <span className="sdl-pill-text">{uploaded} / {total} Documents Uploaded</span>
                </span>
              );
            })()}
          </h3>

          <div>
            {!readonly && (
              <button
                className="button_new m-2"
                style={{ cursor: "pointer" }}
                onClick={addRow}
              >
                <i class="fa-solid fa-plus" style={{ color: "green" }}></i>
                &nbsp;Add
              </button>
            )}
            {/* <button
              className="button_new"
              style={{ cursor: "pointer" }}
              onClick={removeSelectedRows}
            >
              <i class="fa-solid fa-xmark" style={{ color: "red" }}></i>
              &nbsp;Remove
            </button> */}
          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="table table-bordered mt-3">
            <thead>
              <tr>
                {/* <th width="3%"></th> */}
                <th width="5%">S.No</th>
                <th width="15%">Document Name</th>
                <th width="15%">Admin Upload</th>
                <th width="15%">Admin Remark</th>
                <th width="15%">User Upload</th>
                {/* <th>User Remark</th> */}
                <th width="10%">Status</th>
                <th width="10%">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row?.id}-${index}`}>

                  {/* <td>
                    {row?.status == -1 && (
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => handleSelect(row?.id)}
                      />
                    )}
                  </td> */}


                  <td>{index + 1}</td>

                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row?.document_name}
                      disabled={row?.status != -1}
                      onChange={(e) =>
                        handleChange(row?.id, "document_name", e.target.value)
                      }
                    />
                  </td>

                  {/* FILE */}
                  <td>
                    <input
                      type="file"
                      className="form-control"
                      disabled={row?.status != -1}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        handleChange(row?.id, "admin_upload", file);

                        // ✅ AUTO STATUS CHANGE
                        if (file && row?.status === -1) {
                          handleChange(row?.id, "status", -1);
                        }
                      }}
                    />

                    {row?.admin_upload && <small>{row?.admin_upload.name}</small>}

                    {row?.admin_file && !row?.admin_upload && (
                      <div>
                        <OverlayTrigger
                          overlay={
                            <Tooltip id="tooltip-disabled">Download</Tooltip>
                          }
                        >
                          <i
                            className="fa-solid fa-file-arrow-down"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              downloadSingleFile(
                                row?.id,
                                row?.admin_file_name,
                                'admin_file',
                              )
                            }
                            onKeyDown={() =>
                              downloadSingleFile(
                                row?.id,
                                row?.admin_file_name,
                                'admin_file',
                              )
                            }
                          ></i>
                        </OverlayTrigger>
                        <small> {row?.admin_file_name}</small>
                      </div>
                    )}
                  </td>

                  {/* REMARK */}
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row?.admin_remark}
                      disabled={row?.status != -1}
                      onChange={(e) =>
                        handleChange(row?.id, "admin_remark", e.target.value)
                      }
                    />
                  </td>

                  {/* FILE */}
                  <td>
                    {row?.user_file && (row?.status == 0 || row?.status == 1) && (
                      <>
                        <OverlayTrigger
                          overlay={
                            <Tooltip id="tooltip-disabled">Download</Tooltip>
                          }
                        >
                          <i
                            className="fa-solid fa-file-arrow-down"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              downloadSingleFile(
                                row?.id,
                                row?.user_file_name,
                                'user_file',
                              )
                            }
                            onKeyDown={() =>
                              downloadSingleFile(
                                row?.id,
                                row?.user_file_name,
                                'user_file',
                              )
                            }
                          ></i>
                        </OverlayTrigger>
                        <small> {row?.user_file_name}</small>
                      </>
                    )}
                  </td>

                  {/* REMARK */}
                  {/* <td>
                    <span>{row?.user_remark}</span>
                  </td> */}

                  {/* STATUS */}
                  <td>
                    {row?.admin_file && !row?.admin_upload && (
                      <>
                      <span style={{ fontSize: "12px" }}>
                        <i
                          className="fa fa-circle fa-fw"
                          aria-hidden="true"
                          style={{ color: statusConfig[row?.status]?.color }}
                        ></i>{" "}
                        {statusConfig[row?.status]?.label}
                      </span>
                      </>
                    )}
                  </td>

                  {/* ACTION */}
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {!row?.srn_id ? (
                      /* New unsaved row: delete icon calls removeSelectedRows */
                      <button
                        className="button_new"
                        onClick={() => removeSelectedRows(row.id)}
                        style={{ cursor: "pointer", border: "none" }}
                        title="Delete"
                      >
                        <i class="fa-solid fa-trash" style={{ color: "red", fontSize: "15px" }}></i>
                      </button>
                    ) : (row?.status == 0 || row?.status == -1) ? (
                      <>
                        {row?.status == 0 &&
                          <>
                            {/* APPROVE / REJECT only for status 0 */}
                            <button
                              className="button_new"
                              disabled={actionLoader === `${row?.id}-approve`}
                              onClick={() => {
                                setSelectedRowId(row?.id);
                                setStatus(1);
                                handleSignedDocument(row?.id, 1);
                              }}
                              style={{ cursor: "pointer" }}
                              title="Approve"
                            >
                              {actionLoader === `${row?.id}-approve` ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i class="fa-solid fa-check" style={{ color: "green", fontSize: "12px" }}></i>
                              )}
                            </button>
                            &nbsp;
                            <button
                              className="reject_button_new"
                              disabled={actionLoader === `${row?.id}-reject`}
                              onClick={() => {
                                setSelectedRowId(row?.id);
                                setStatus(2);
                                setRejectRemarks("");
                                setRejectModal(true);
                              }}
                              style={{ cursor: "pointer" }}
                              title="Reject"
                            >
                              {actionLoader === `${row?.id}-reject` ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i class="fa-solid fa-xmark" style={{ color: "red", fontSize: "15px" }}></i>
                              )}
                            </button>
                          </>
                        }
                        {/* DELETE for existing row with srn_id and no user file */}
                        {!row?.user_file && !readonly && (
                          <button
                            className="button_new"
                            disabled={actionLoader === `${row?.id}-delete`}
                            onClick={() => setDeleteConfirmId(row?.id)}
                            style={{ cursor: "pointer", border: "none" }}
                            title="Delete"
                          >
                            {actionLoader === `${row?.id}-delete` ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <i class="fa-solid fa-trash" style={{ color: "red", fontSize: "15px" }}></i>
                            )}
                          </button>
                        )}
                      </>
                    ) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!readonly && (
          <div className="text-end">
            <button
              className="btn button_new mt-2"
              style={{ background: hasChanges ? "green" : "#aaa", color: "white", cursor: hasChanges ? "pointer" : "not-allowed" }}
              onClick={handleSubmit}
              disabled={!hasChanges || !!loader}
            >
              {loader || "Save"}
            </button>
          </div>
        )}
      </div >

      <Modal show={!!deleteConfirmId} onHide={() => setDeleteConfirmId(null)} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this item?</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setDeleteConfirmId(null)}>
            No
          </Button>
          <Button
            variant="success"
            disabled={actionLoader === `${deleteConfirmId}-delete`}
            onClick={() => {
              const id = deleteConfirmId;
              setDeleteConfirmId(null);
              setSelectedRowId(id);
              setStatus(3);
              handleSignedDocument(id, 3);
            }}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div >
  );
};

export default SignedDocumentList;
